/**
 * TemplateSectionItem Component
 *
 * Individual section card in the template editor for the V2 system.
 * Sections have inline content (no module references).
 *
 * Features:
 * - Display section title and content preview
 * - Left-side drag handle for reordering using @dnd-kit
 * - Expand/collapse example output preview
 * - Remove action
 */

import React, { useState } from 'react';
import {
  DragHandle,
  TemplateSectionCard,
  TemplateSectionContent,
  TemplateSectionHeader,
} from '@components/TemplateSections';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Close as CloseIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import type { SectionWithMeta } from '../types';

interface TemplateSectionItemProps {
  /** Section data with metadata */
  section: SectionWithMeta;
  /** Whether section can be edited */
  canEdit: boolean;
  /** Callback to remove section */
  onRemove?: () => void;
}

export const TemplateSectionItem: React.FC<TemplateSectionItemProps> = ({
  section,
  canEdit,
  onRemove,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
    animateLayoutChanges: () => false, // Disable layout animations for better performance
    disabled: !canEdit, // Disable sorting when can't edit
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const hasExampleOutput = Boolean(section.exampleOutput && section.exampleOutput.trim().length > 0);

  return (
    <TemplateSectionCard
      variant="card"
      isDragging={isDragging}
      nodeRef={setNodeRef}
      style={style}
      dragHandleSlot={
        canEdit ? (
          <DragHandle
            listeners={listeners}
            attributes={attributes}
            size="small"
          />
        ) : undefined
      }
    >
      <TemplateSectionHeader
        title={section.title}
        collapsible={hasExampleOutput}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
        actions={
          canEdit && onRemove ? (
            <IconButton
              size="small"
              onClick={handleRemove}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
      />
      {hasExampleOutput && (
        <TemplateSectionContent
          markdown={section.exampleOutput}
          collapsed={!isExpanded}
          isExample={true}
        />
      )}
    </TemplateSectionCard>
  );
};
